import { Radio, RadioGroup, Spacing, Text, Title } from '@vkontakte/vkui';
import classNames from 'clsx';
import React, { memo, useCallback, useEffect, useState } from 'react';
import { emitAnalyticEvent } from 'reactApp/appHelpers/experimentAnalytic';
import { AnalyticEventNames } from 'reactApp/appHelpers/experimentAnalytic/eventNames';
import { BaseConfirmDialog } from 'reactApp/components/BaseConfirmDialog/BaseConfirmDialog';
import { EButtonType } from 'reactApp/components/BaseConfirmDialog/BaseConformationDialog.types';
import {
    type MultiTariffUpsaleModelType,
    MultiTariffUpsaleRadioType,
} from 'reactApp/sections/QuotaLanding/multiTariffUpsaleDialog/models/MultiTariffUpsaleDialog.model.types';
import { sendQuotaGa } from 'reactApp/sections/QuotaLanding/QuotaLanding.helpers';
import type { Product } from 'reactApp/types/Billing';
import { isDarkThemeModeEnabled } from 'reactApp/utils/theme';

import styles from './MultiTariffUpsaleDialog.css';

export type MultiTariffUpsaleDialogProps = {
    upsaleModel: MultiTariffUpsaleModelType;
    onClose?: () => void;
    resolve: (product: Product) => void;
};

export const MultiTariffUpsaleDialog = memo<MultiTariffUpsaleDialogProps>(({ upsaleModel, onClose, resolve }) => {
    const title = upsaleModel.getTitle();
    const text = upsaleModel.getTitleDescription();
    const secondProduct = upsaleModel.getSecondProduct();
    const upsaleProduct = upsaleModel.getUpsaleProduct();

    const [chosenTariff, setChosenTariff] = useState<MultiTariffUpsaleRadioType>(MultiTariffUpsaleRadioType.priority);
    const isPriority = chosenTariff === MultiTariffUpsaleRadioType.priority;

    const sendAnalitics = (label: string, data: MultiTariffUpsaleRadioType) => {
        sendQuotaGa({
            label,
            ...upsaleModel.getDwh(data),
        });
    };

    useEffect(() => {
        sendAnalitics('show', chosenTariff);
        emitAnalyticEvent(AnalyticEventNames.UPSALE_MULTI_MODAL_SHOW);
    }, []);

    const switchTariff = useCallback(
        (tariff: MultiTariffUpsaleRadioType) => {
            setChosenTariff(tariff);
            sendAnalitics('switch', tariff);
            emitAnalyticEvent(AnalyticEventNames.UPSALE_MULTI_MODAL_SWITCH);
        },
        [setChosenTariff]
    );

    const renderHeader = useCallback(
        () => (
            <>
                <Title>{title}</Title>
                <Spacing size={12} />
                <Text className={styles.headerText}>{text}</Text>
            </>
        ),
        [title, text]
    );

    const renderContent = useCallback(() => {
        return (
            <RadioGroup className={styles.radioGroup}>
                <Radio
                    className={classNames(styles.radio, {
                        [styles['radio_active']]: isPriority,
                    })}
                    name="activePeriod"
                    value="year"
                    description={
                        <>
                            <Text className={styles.radioDescription}>{upsaleModel.getPriorityCardTopTitle()}</Text>
                            <Title className={styles.radioTitle}>{upsaleModel.getPriorityCardTitle()}</Title>
                            <Text className={styles.radioDescription}>{upsaleModel.getPriorityCardDescription()}</Text>
                        </>
                    }
                    checked={isPriority}
                    onClick={() => switchTariff(MultiTariffUpsaleRadioType.priority)}
                >
                    <div
                        className={classNames(styles.label, {
                            [styles.labelActive]: isPriority,
                        })}
                    >
                        {upsaleModel.getPriorityCardLabelText()}
                    </div>
                </Radio>

                <Radio
                    className={classNames(styles.radio, {
                        [styles['radio_active']]: !isPriority,
                    })}
                    name="activePeriod"
                    value="month"
                    description={
                        <div className={styles.radioWrapper}>
                            <Text className={styles.radioDescription}>{upsaleModel.getSecondaryCardTopTitle()}</Text>
                            <Title className={styles.radioTitle}>{upsaleModel.getSecondaryCardTitle()}</Title>
                            <Text className={styles.radioDescription}>{upsaleModel.getSecondaryCardDescription()}</Text>
                        </div>
                    }
                    checked={!isPriority}
                    onClick={() => switchTariff(MultiTariffUpsaleRadioType.second)}
                >
                    <div
                        className={classNames(styles.label, {
                            [styles.labelActive]: !isPriority,
                            [styles.labelNone]: !upsaleModel.getSecondaryCardLabelVisible(),
                        })}
                    >
                        {upsaleModel.getSecondaryCardLabelText()}
                    </div>
                </Radio>
            </RadioGroup>
        );
    }, [chosenTariff]);

    const handleClose = useCallback(() => {
        onClose?.();
        sendAnalitics('close', chosenTariff);
        emitAnalyticEvent(AnalyticEventNames.UPSALE_MULTI_MODAL_CLOSE);
    }, [onClose]);

    const handleSuccess = useCallback(() => {
        sendAnalitics('continue', chosenTariff);
        emitAnalyticEvent(AnalyticEventNames.UPSALE_MULTI_MODAL__CONTINUE);
        onClose?.();

        const productToResolve = isPriority ? upsaleProduct : secondProduct;

        if (productToResolve) {
            resolve(productToResolve);
        }
    }, [chosenTariff, secondProduct, upsaleProduct, isPriority]);

    const containsDarkTheme = isDarkThemeModeEnabled();

    return (
        <BaseConfirmDialog
            onClose={handleClose}
            closeOnDimmerClick
            renderHeader={renderHeader}
            renderContent={renderContent}
            buttons={[EButtonType.accept]}
            buttonTheme="vk"
            successButtonText="Продолжить"
            onSuccess={handleSuccess}
            isDarkTheme={containsDarkTheme}
            className={styles.root}
        />
    );
});

MultiTariffUpsaleDialog.displayName = 'MultiTariffUpsaleDialog';
